import type { Features } from "@/lib/features"

/**
 * Appointments (AMS booking) is in preview. CMS staff see it only when the `AmsBooking` feature
 * flag is on — with it off the API has no appointments routes — and they have been granted
 * `appointments:view` or `appointments:manage`, directly or through an appointments-only role
 * (Scheduling Manager, Appointment Viewer); no general role carries either.
 */

/**
 * Either permission opens the appointments section: `appointments:manage` counts because the API
 * treats it as implying view, so a direct grant of manage alone is still deliberate access.
 */
export const APPOINTMENTS_ACCESS_PERMISSIONS = ["appointments:view", "appointments:manage"]

/**
 * Opens the Data Gaps page and the reconciler and scheduling-link tools behind it. Direct-grant only:
 * no role carries it, and it neither implies nor is implied by view or manage, so an administrator
 * also needs one of {@link APPOINTMENTS_ACCESS_PERMISSIONS} to reach the appointments section.
 */
export const APPOINTMENTS_ADMIN_PERMISSION = "appointments:admin"

/**
 * Books appointments from the staff calendar onto Public and Private slots (AB#779). Carried by both
 * appointments roles and implied by `appointments:manage` at the API, so either code books here. A
 * Managed slot and the double-booking and duplicate-household overrides stay manage's.
 */
export const APPOINTMENTS_BOOK_PERMISSION = "appointments:book"

/** Administers the schedule: every slot type, and both booking overrides. */
export const APPOINTMENTS_MANAGE_PERMISSION = "appointments:manage"

/** Whether this user may book from the staff calendar at all — book, or manage, which implies it. */
export function canBookAppointments(permissions: string[]): boolean {
  return permissions.includes(APPOINTMENTS_BOOK_PERMISSION) || permissions.includes(APPOINTMENTS_MANAGE_PERMISSION)
}

/** Whether this user may book a Managed slot, double-book a host, or book a household twice. */
export function canManageAppointments(permissions: string[]): boolean {
  return permissions.includes(APPOINTMENTS_MANAGE_PERMISSION)
}

/** Whether the appointments section is open to this user. */
export function canAccessAppointments(permissions: string[], features: Features): boolean {
  return features.amsBooking && APPOINTMENTS_ACCESS_PERMISSIONS.some(code => permissions.includes(code))
}
